/**
 * `GraphSavedViewsBar` — the save / apply / share toolbar for any graph surface.
 *
 * One reusable control that composes the two lower packages the dashboard already
 * depends on: it drives a `@authe/saved-views` graph controller (`useGraphViews`)
 * and snapshots a `@authe/tx-graph` `GraphShareState`. Drop it next to any
 * `GraphCanvas` (the overview activity panel, a transaction's flow, the dedicated
 * /graphs page) to let the user:
 *
 *  - **Save as view** — snapshot the graph currently on screen as a personal or
 *    tenant-shared saved view (`SaveCurrentViewDialog` → `controller.save`).
 *  - **Apply** — switch to / reset from a saved view via the `SavedViewsMenu`.
 *    Applying replaces the host's native/live graph with the saved snapshot; the
 *    menu's *Reset* clears it (`onApplyGraph(null)`) so the host shows live again.
 *  - **Share** — mint tokenized share links for the active saved view
 *    (`ShareViewDialog`), with audience / password / expiry / max-views.
 *
 * The bar owns the saved-views state; the host only supplies the graph to
 * snapshot (`currentGraph`) and reacts to applies (`onApplyGraph`) — keeping the
 * saved-views and tx-graph packages decoupled (the dashboard composes them).
 */
import * as React from "react";
import { Share2 } from "lucide-react";
import {
  useGraphViews,
  SaveCurrentViewDialog,
  SavedViewsMenu,
  ShareViewDialog,
  type AccessWarning,
  type SavedView,
  type ViewScope,
  type ViewState,
} from "@authe/saved-views";
import { buildGraphViewServing, type GraphShareState } from "@authe/tx-graph";
import { Button, cn } from "@authe/ui-core";
import { useSession } from "@authe/ui-core/session";
import { useSavedViewsSource } from "@/api/adapters";
import { RESOURCES } from "@/config";

/** Read the graph payload + access warnings a saved view-state carries. */
export function readGraphState(state: ViewState | null | undefined): {
  graph: GraphShareState | null;
  warnings: AccessWarning[];
} {
  const extra = (state?.extra ?? null) as
    | (GraphShareState & { warnings?: AccessWarning[] })
    | null;
  return { graph: extra, warnings: extra?.warnings ?? [] };
}

export interface GraphSavedViewsBarProps {
  /** Saved-views "table" the graph views live under. Default `RESOURCES.graphs`. */
  tableId?: string;
  /** Snapshot of the graph currently shown — frozen into a view when the user saves. */
  currentGraph: GraphShareState;
  /** Render a saved view's graph (`null` = reset to the host's native/live graph). */
  onApplyGraph: (graph: GraphShareState | null) => void;
  /** Surface a shared view's access warnings to the host (optional). */
  onWarnings?: (warnings: AccessWarning[]) => void;
  /** Apply the default/last view on mount — the dedicated /graphs page wants this. */
  autoApplyDefault?: boolean;
  /** Disable saving (e.g. nothing on the canvas yet). */
  saveDisabled?: boolean;
  className?: string;
}

export function GraphSavedViewsBar({
  tableId = RESOURCES.graphs,
  currentGraph,
  onApplyGraph,
  onWarnings,
  autoApplyDefault = false,
  saveDisabled = false,
  className,
}: GraphSavedViewsBarProps): React.ReactElement {
  const source = useSavedViewsSource();
  const { session } = useSession();
  const [saveOpen, setSaveOpen] = React.useState(false);
  const [shareOpen, setShareOpen] = React.useState(false);

  const onWarningsRef = React.useRef(onWarnings);
  onWarningsRef.current = onWarnings;
  const onApplyGraphRef = React.useRef(onApplyGraph);
  onApplyGraphRef.current = onApplyGraph;

  const onApply = React.useCallback((state: ViewState, view: SavedView | null) => {
    const next = readGraphState(state);
    onApplyGraphRef.current(view ? next.graph : null);
    onWarningsRef.current?.(view ? next.warnings : []);
  }, []);

  const controller = useGraphViews({
    source,
    tableId,
    onApply,
    autoLoad: autoApplyDefault,
    identity: {
      app: "dashboard",
      orgId: session?.tenant.orgId ?? null,
      tenantId: session?.tenant.tenantId ?? null,
      userId: session?.user.id ?? null,
    },
  });

  // When we don't auto-apply on mount, still populate the switcher so the user
  // can pick a view (refresh loads the list without applying anything).
  const refresh = controller.refresh;
  React.useEffect(() => {
    if (!autoApplyDefault) refresh();
  }, [autoApplyDefault, refresh]);

  const handleSave = React.useCallback(
    async (name: string, scope: ViewScope): Promise<void> => {
      const state: ViewState = { extra: { ...currentGraph } };
      // Persist both a frozen snapshot and a live re-fetch seed so the saved
      // view backs the full share matrix (a link later picks static vs dynamic).
      const serving = buildGraphViewServing(currentGraph);
      await controller.save(name, state, scope, serving);
    },
    [controller, currentGraph],
  );

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <SavedViewsMenu
        controller={controller}
        onSaveCurrent={() => {
          if (!saveDisabled) setSaveOpen(true);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={!controller.activeViewId}
        onClick={() => setShareOpen(true)}
        title={controller.activeViewId ? "Share this view" : "Save a view first to share it"}
      >
        <Share2 className="h-3.5 w-3.5" />
        Share
      </Button>

      <SaveCurrentViewDialog open={saveOpen} onOpenChange={setSaveOpen} onSave={handleSave} />
      <ShareViewDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        source={source}
        viewId={controller.activeViewId}
        allowDynamic={Boolean(controller.activeView?.dynamicSeed)}
      />
    </div>
  );
}
